import { getLocalTimeZone, parseDate } from '@internationalized/date';
import {
  ALL_NEWSLETTER_CHANNELS,
  ALL_NEWSLETTER_STATUSES,
  type NewsletterListParams,
  type NewsletterStatusFilter,
} from '@/services/db/newsletter/entity';
import type { ExportsNewsletterDeliveryTotalsRequest } from '@customerio/hydra-ui-api';
import type { ApiTriggeredBroadcastListParams } from '@/services/db/campaign/entity';
import { searchString } from '@/services/router/search-scalars';
import { parseSendVolumeType, type SendVolumeType } from './send-volume/send-volume-types';
import { isEmptyString } from '@/utils/string';

/**
 * Both Broadcasts tabs keep their filters in the URL, in the shape the embedded app writes
 * them: an id list is a JSON array (`tagIds=["1","2"]`), a flag is `true`/`false`, a date is
 * `YYYY-MM-DD`, and a parameter at its default is left out. The router's own search
 * serializer produces exactly that, and the flag-gated handoff between the two apps copies
 * the query string across verbatim, so a filtered link opens the same list in either app.
 */

/** Rows per page, for both tabs. The two list endpoints page by the same fixed size. */
export const BROADCASTS_PAGE_SIZE = 20;

/** Both tabs sort by last-updated, newest first, until the reader picks otherwise. */
export const DEFAULT_BROADCASTS_SORT = 'updated' as const;

const NEWSLETTER_STATUS_FILTERS: readonly NewsletterStatusFilter[] = ['all', 'drafted', 'scheduled', 'sent'];

/**
 * A route's search as `validateSearch` receives it: unparsed input, which is spread back
 * through so global params the page doesn't own (`masq_account_id`, …) survive.
 */
// oxlint-disable-next-line local-rules/no-unsafe-dictionary-type -- validateSearch is handed unparsed search input, and the pass-through carries those same unvalidated values
export type RawSearch = Record<string, unknown>;

/** The filters both tabs share. */
type SharedBroadcastsSearch = {
  /** Server-side name search. Shared between the tabs: switching carries it across. */
  search?: string;
  /** 1-based. */
  page?: number;
  asc?: boolean;
  tagIds?: string[];
  sendToUnsubscribed?: boolean;
  subscriptionTopicId?: number;
  objectTypeIds?: string[];
  goalIds?: string[];
};

/** The one-time sends tab's search params. */
export type NewslettersSearch = SharedBroadcastsSearch & {
  sort?: string;
  /** A channel name, or `all`. */
  channel?: string;
  type?: NewsletterStatusFilter;
  /** `YYYY-MM-DD`, the first day of the range. */
  startTime?: string;
  /** `YYYY-MM-DD`, the last day of the range, inclusive. */
  endTime?: string;
};

/** The API-triggered tab's search params. */
export type ApiTriggeredSearch = SharedBroadcastsSearch & {
  sort?: ApiTriggeredBroadcastListParams['sort'];
  state?: ApiTriggeredBroadcastListParams['state'];
};

/**
 * The Send volume tab's search params. Its `type` is a broadcast type, not the one-time sends
 * tab's status filter of the same name, which is why each tab validates its own.
 */
export type SendVolumeSearch = {
  search?: string;
  type?: SendVolumeType;
  /** Comma-separated sender tokens to keep; see `SenderSelection`. */
  include?: string;
  /** Comma-separated sender tokens to drop. Ignored while `include` is set. */
  exclude?: string;
};

/** Every sort the campaigns list accepts. A value outside it would 400, so it is dropped. */
const API_TRIGGERED_SORTS: ReadonlyArray<NonNullable<ApiTriggeredSearch['sort']>> = [
  'name',
  'created',
  'updated',
  'sent',
  'delivered',
  'opened',
  'clicked',
  'attempted',
  'converted',
  'bounced',
  'failed',
  'spammed',
  'undeliverable',
  'unsubscribed',
  'topic_unsubscribed',
];

const API_TRIGGERED_STATES: ReadonlyArray<NonNullable<ApiTriggeredSearch['state']>> = [
  'draft',
  'running',
  'scheduled',
  'stopped',
];

/** An id list. The router JSON-parses each value, so an all-digit id can arrive as a number. */
function searchIds(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const ids = value.flatMap((id) => {
    const text = searchString(id);
    return text == null || text === '' ? [] : [text];
  });
  return ids.length > 0 ? ids : undefined;
}

function searchBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return undefined;
}

function searchInteger(value: unknown, { min }: { min: number }): number | undefined {
  const number = typeof value === 'number' ? value : Number(searchString(value));
  return Number.isInteger(number) && number >= min ? number : undefined;
}

/** A `YYYY-MM-DD` date that exists. An impossible one (`2026-02-30`) is dropped like any other bad value. */
function searchDate(value: unknown): string | undefined {
  const date = searchString(value);
  if (date == null || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return undefined;
  }
  try {
    return parseDate(date).toString() === date ? date : undefined;
  } catch {
    return undefined;
  }
}

function oneOf<T extends string>(allowed: readonly T[], value: unknown): T | undefined {
  const text = searchString(value);
  return allowed.find((candidate) => candidate === text);
}

function validateSharedSearch(search: RawSearch): SharedBroadcastsSearch {
  const text = searchString(search.search);
  return {
    search: text === '' ? undefined : text,
    page: searchInteger(search.page, { min: 1 }),
    asc: searchBoolean(search.asc),
    tagIds: searchIds(search.tagIds),
    sendToUnsubscribed: searchBoolean(search.sendToUnsubscribed),
    // 0 is the topic filter's "None": sends with no topic assigned.
    subscriptionTopicId: searchInteger(search.subscriptionTopicId, { min: 0 }),
    objectTypeIds: searchIds(search.objectTypeIds),
    goalIds: searchIds(search.goalIds),
  };
}

/**
 * `validateSearch` for the one-time sends tab. The incoming search is spread so global params
 * the page doesn't own (`masq_account_id`, …) survive; only this tab's are narrowed.
 */
export function validateNewslettersSearch(search: RawSearch): RawSearch & NewslettersSearch {
  return {
    ...search,
    ...validateSharedSearch(search),
    sort: searchString(search.sort),
    channel: searchString(search.channel),
    type: oneOf(NEWSLETTER_STATUS_FILTERS, search.type),
    startTime: searchDate(search.startTime),
    endTime: searchDate(search.endTime),
  };
}

/** `validateSearch` for the API-triggered tab. */
export function validateApiTriggeredSearch(search: RawSearch): RawSearch & ApiTriggeredSearch {
  return {
    ...search,
    ...validateSharedSearch(search),
    sort: oneOf(API_TRIGGERED_SORTS, search.sort),
    state: oneOf(API_TRIGGERED_STATES, search.state),
  };
}

/** `validateSearch` for the Send volume tab. */
export function validateSendVolumeSearch(search: RawSearch): RawSearch & SendVolumeSearch {
  const text = searchString(search.search);
  const include = searchString(search.include);
  const exclude = searchString(search.exclude);
  const type = parseSendVolumeType(searchString(search.type));
  return {
    ...search,
    search: isEmptyString(text) ? undefined : text,
    type: type === 'all' ? undefined : type,
    include: isEmptyString(include) ? undefined : include,
    exclude: isEmptyString(exclude) ? undefined : exclude,
  };
}

/**
 * The search with every parameter at its default left out, so the URL reads the way the
 * embedded app writes it and two searches that mean the same thing compare equal.
 */
export function withoutDefaults<T extends NewslettersSearch | ApiTriggeredSearch>(search: T): T {
  const cleaned = {
    ...search,
    search: search.search === '' ? undefined : search.search,
    page: search.page === 1 ? undefined : search.page,
    sort: search.sort === DEFAULT_BROADCASTS_SORT ? undefined : search.sort,
    asc: search.asc === false ? undefined : search.asc,
    tagIds: search.tagIds?.length === 0 ? undefined : search.tagIds,
    objectTypeIds: search.objectTypeIds?.length === 0 ? undefined : search.objectTypeIds,
    goalIds: search.goalIds?.length === 0 ? undefined : search.goalIds,
  };
  if ('channel' in cleaned && cleaned.channel === ALL_NEWSLETTER_CHANNELS) {
    cleaned.channel = undefined;
  }
  if ('type' in cleaned && cleaned.type === ALL_NEWSLETTER_STATUSES) {
    cleaned.type = undefined;
  }
  return cleaned;
}

/** Local midnight at the start of `date`, in unix seconds. */
function startOfLocalDayUnix(date: string): number {
  return Math.floor(parseDate(date).toDate(getLocalTimeZone()).getTime() / 1000);
}

/** The last second of `date`, local time. */
function endOfLocalDayUnix(date: string): number {
  return startOfLocalDayUnix(parseDate(date).add({ days: 1 }).toString()) - 1;
}

type ResolveOptions = {
  /**
   * Whether the Goals filter exists for this account. Without it a goal id — stale, or typed
   * into the URL — is dropped from the request, since there would be no control to see or
   * clear it.
   */
  showGoals: boolean;
};

/**
 * Whether the reader has narrowed the list, as the empty states read it: by search, tags,
 * subscription, topic, object types, goals or, on the API-triggered tab, state. Dates never count,
 * as they don't in Ember. The one-time sends tab adds its own channel and status.
 */
export function hasAnyBroadcastsFilters(search: SharedBroadcastsSearch & { state?: string }): boolean {
  return [
    !isEmptyString(search.search),
    (search.tagIds?.length ?? 0) > 0,
    search.sendToUnsubscribed != null,
    !isEmptyString(search.state),
    search.subscriptionTopicId != null,
    (search.objectTypeIds?.length ?? 0) > 0,
    (search.goalIds?.length ?? 0) > 0,
  ].some(Boolean);
}

/** Whether the one-time sends list is narrowed: the shared filters, or a channel or status. */
export function hasAnyNewslettersFilters(search: NewslettersSearch): boolean {
  return (
    hasAnyBroadcastsFilters(search) ||
    (search.type ?? ALL_NEWSLETTER_STATUSES) !== ALL_NEWSLETTER_STATUSES ||
    (search.channel ?? ALL_NEWSLETTER_CHANNELS) !== ALL_NEWSLETTER_CHANNELS
  );
}

/** The one-time sends read these params make, defaults filled in. */
export function toNewsletterListParams(search: NewslettersSearch, { showGoals }: ResolveOptions): NewsletterListParams {
  return {
    page: search.page ?? 1,
    channel: search.channel ?? ALL_NEWSLETTER_CHANNELS,
    type: search.type ?? ALL_NEWSLETTER_STATUSES,
    search: search.search,
    sort: search.sort ?? DEFAULT_BROADCASTS_SORT,
    asc: search.asc ?? false,
    // Each bound on its own, as Ember's route sends them: a URL with only a start still reads from it.
    startTime: search.startTime == null ? undefined : startOfLocalDayUnix(search.startTime),
    endTime: search.endTime == null ? undefined : endOfLocalDayUnix(search.endTime),
    sendToUnsubscribed: search.sendToUnsubscribed,
    subscriptionTopicId: search.subscriptionTopicId,
    tagIds: search.tagIds,
    objectTypeIds: search.objectTypeIds,
    goalIds: showGoals ? search.goalIds : undefined,
  };
}

/**
 * The one-time sends CSV export's body, as Ember's export button builds it: the search when there
 * is one, each date bound on its own, and the channel. The list's other filters (tags, status,
 * topic, object types, goals) aren't sent; the server exports only sent sends.
 */
export function toNewslettersExportBody(search: NewslettersSearch): ExportsNewsletterDeliveryTotalsRequest {
  return {
    exported_from: 'newsletters_list',
    channel: search.channel ?? ALL_NEWSLETTER_CHANNELS,
    search: isEmptyString(search.search) ? undefined : search.search,
    start_time: search.startTime == null ? undefined : startOfLocalDayUnix(search.startTime),
    end_time: search.endTime == null ? undefined : endOfLocalDayUnix(search.endTime),
  };
}

/** The API-triggered read these params make, defaults filled in. */
export function toApiTriggeredListParams(
  search: ApiTriggeredSearch,
  { showGoals }: ResolveOptions,
): ApiTriggeredBroadcastListParams {
  return {
    page: search.page ?? 1,
    state: search.state,
    search: search.search,
    sort: search.sort ?? DEFAULT_BROADCASTS_SORT,
    asc: search.asc ?? false,
    sendToUnsubscribed: search.sendToUnsubscribed,
    subscriptionTopicId: search.subscriptionTopicId,
    tagIds: search.tagIds,
    objectTypeIds: search.objectTypeIds,
    goalIds: showGoals ? search.goalIds : undefined,
  };
}
